import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { MetricCard } from "@/components/analytics/metric-card";
import { TimeSeriesChart } from "@/components/analytics/time-series-chart";
import { PipelineCard } from "@/components/crm/pipeline-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccessProfile } from "@/hooks/use-admin-auth";
import { resolveHomeRedirect } from "@/lib/nav-visibility";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/")({
	component: DashboardPage,
});

type DashboardOverview = {
	summary: {
		totalUsers: {
			current: number;
			trend: number | null;
			data: Array<{ timestamp: string; value: number }>;
		};
		totalOrganizations: {
			current: number;
			trend: number | null;
			data: Array<{ timestamp: string; value: number }>;
		};
		totalProjects: {
			current: number;
			trend: number | null;
			data: Array<{ timestamp: string; value: number }>;
		};
		activeSubscriptions: {
			current: number;
			trend: number | null;
			data: Array<{ timestamp: string; value: number }>;
		};
		userChurnRate: {
			current: number;
			trend: number | null;
			data: Array<{ timestamp: string; value: number }>;
		};
		organizationChurnRate: {
			current: number;
			trend: number | null;
			data: Array<{ timestamp: string; value: number }>;
		};
		subscriptionChurnRate: {
			current: number;
			trend: number | null;
			data: Array<{ timestamp: string; value: number }>;
		};
	};
	timeRange: {
		startDate: string;
		endDate: string;
		days: number;
	};
};

type ClientWithAnalytics = typeof client & {
	admin: {
		analytics: {
			getDashboardOverview: (input: {
				days: number;
			}) => Promise<DashboardOverview>;
		};
	};
};

function DashboardPage() {
	const { t } = useTranslation();

	// The dashboard's data is support-gated — a sales- or system-only user
	// entering the app must be redirected before the analytics query 403s.
	const { profile, isLoading: profileLoading } = useAccessProfile();
	// A CRM-only role (crm_admin) lands in the CRM (profile.canAccessCrm).
	const redirectTo = profile ? resolveHomeRedirect(profile) : null;

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "analytics", "dashboard"],
		queryFn: async () => {
			return await (
				client as ClientWithAnalytics
			).admin.analytics.getDashboardOverview({
				days: 30,
			});
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
		enabled: !!profile && !redirectTo,
	});

	if (redirectTo) {
		return <Navigate to={redirectTo} />;
	}
	if (profileLoading) {
		return null;
	}

	if (isLoading) {
		return (
			<div className="space-y-4">
				<div>
					<h1 className="font-bold text-3xl">{t("dashboard:welcome")}</h1>
					<p className="text-muted-foreground">{t("admin:title")}</p>
				</div>
				<div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
					<Skeleton className="h-32" />
					<Skeleton className="h-32" />
					<Skeleton className="h-32" />
					<Skeleton className="h-32" />
				</div>
				<div className="grid gap-3 md:grid-cols-2">
					<Skeleton className="h-64" />
					<Skeleton className="h-64" />
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div>
				<h1 className="font-bold text-3xl">{t("dashboard:title")}</h1>
			</div>

			<PipelineCard />

			{/* Growth Metrics */}
			<div>
				<h2 className="mb-3 font-semibold text-lg">
					{t("dashboard:sections.growth_metrics")}
				</h2>
				<div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
					<MetricCard
						title={t("dashboard:stats.total_users")}
						description={t("dashboard:stats.total_users_description", {
							days: data?.timeRange.days || 30,
						})}
						value={data?.summary.totalUsers.current || 0}
						trend={data?.summary.totalUsers.trend}
					/>
					<MetricCard
						title={t("dashboard:stats.total_organizations")}
						description={t("dashboard:stats.total_organizations_description", {
							days: data?.timeRange.days || 30,
						})}
						value={data?.summary.totalOrganizations.current || 0}
						trend={data?.summary.totalOrganizations.trend}
					/>
					<MetricCard
						title={t("dashboard:stats.total_projects")}
						description={t("dashboard:stats.total_projects_description", {
							days: data?.timeRange.days || 30,
						})}
						value={data?.summary.totalProjects.current || 0}
						trend={data?.summary.totalProjects.trend}
					/>
					<MetricCard
						title={t("dashboard:stats.active_subscriptions")}
						description={t("dashboard:stats.active_subscriptions_description", {
							days: data?.timeRange.days || 30,
						})}
						value={data?.summary.activeSubscriptions.current || 0}
						trend={data?.summary.activeSubscriptions.trend}
					/>
				</div>
			</div>

			{/* Churn & Retention Metrics */}
			<div>
				<h2 className="mb-3 font-semibold text-lg">
					{t("dashboard:sections.churn_metrics")}
				</h2>
				<div className="grid gap-3 md:grid-cols-3">
					<MetricCard
						title={t("dashboard:stats.user_churn_rate")}
						description={t("dashboard:stats.user_churn_rate_description", {
							days: data?.timeRange.days || 30,
						})}
						value={data?.summary.userChurnRate.current || 0}
						trend={data?.summary.userChurnRate.trend}
						formatValue={(v) => `${v.toFixed(2)}%`}
					/>
					<MetricCard
						title={t("dashboard:stats.organization_churn_rate")}
						description={t(
							"dashboard:stats.organization_churn_rate_description",
							{
								days: data?.timeRange.days || 30,
							},
						)}
						value={data?.summary.organizationChurnRate.current || 0}
						trend={data?.summary.organizationChurnRate.trend}
						formatValue={(v) => `${v.toFixed(2)}%`}
					/>
					<MetricCard
						title={t("dashboard:stats.subscription_churn_rate")}
						description={t(
							"dashboard:stats.subscription_churn_rate_description",
							{
								days: data?.timeRange.days || 30,
							},
						)}
						value={data?.summary.subscriptionChurnRate.current || 0}
						trend={data?.summary.subscriptionChurnRate.trend}
						formatValue={(v) => `${v.toFixed(2)}%`}
					/>
				</div>
			</div>

			{/* Growth Charts */}
			<div>
				<h2 className="mb-3 font-semibold text-lg">
					{t("dashboard:sections.growth_trends")}
				</h2>
				<div className="grid gap-3 md:grid-cols-2">
					<TimeSeriesChart
						title={t("dashboard:charts.users_growth")}
						description={t("dashboard:charts.users_growth_description", {
							days: data?.timeRange.days || 30,
						})}
						data={data?.summary.totalUsers.data || []}
						color="#3b82f6"
					/>
					<TimeSeriesChart
						title={t("dashboard:charts.organizations_growth")}
						description={t(
							"dashboard:charts.organizations_growth_description",
							{
								days: data?.timeRange.days || 30,
							},
						)}
						data={data?.summary.totalOrganizations.data || []}
						color="#10b981"
					/>
					<TimeSeriesChart
						title={t("dashboard:charts.projects_growth")}
						description={t("dashboard:charts.projects_growth_description", {
							days: data?.timeRange.days || 30,
						})}
						data={data?.summary.totalProjects.data || []}
						color="#f59e0b"
					/>
					<TimeSeriesChart
						title={t("dashboard:charts.subscriptions_growth")}
						description={t(
							"dashboard:charts.subscriptions_growth_description",
							{
								days: data?.timeRange.days || 30,
							},
						)}
						data={data?.summary.activeSubscriptions.data || []}
						color="#8b5cf6"
					/>
				</div>
			</div>

			{/* Churn Charts */}
			<div>
				<h2 className="mb-3 font-semibold text-lg">
					{t("dashboard:sections.churn_trends")}
				</h2>
				<div className="grid gap-3 md:grid-cols-3">
					<TimeSeriesChart
						title={t("dashboard:charts.user_churn")}
						description={t("dashboard:charts.user_churn_description", {
							days: data?.timeRange.days || 30,
						})}
						data={data?.summary.userChurnRate.data || []}
						color="#ef4444"
						formatValue={(v) => `${v.toFixed(2)}%`}
					/>
					<TimeSeriesChart
						title={t("dashboard:charts.organization_churn")}
						description={t("dashboard:charts.organization_churn_description", {
							days: data?.timeRange.days || 30,
						})}
						data={data?.summary.organizationChurnRate.data || []}
						color="#f97316"
						formatValue={(v) => `${v.toFixed(2)}%`}
					/>
					<TimeSeriesChart
						title={t("dashboard:charts.subscription_churn")}
						description={t("dashboard:charts.subscription_churn_description", {
							days: data?.timeRange.days || 30,
						})}
						data={data?.summary.subscriptionChurnRate.data || []}
						color="#dc2626"
						formatValue={(v) => `${v.toFixed(2)}%`}
					/>
				</div>
			</div>
		</div>
	);
}
